import { useCallback, useMemo, useState } from "react";
import {
  PARTICIPANT_TILE_KIND,
  ROOM_ADMIN_ROLE_LABEL,
  SCREEN_SHARE_TILE_ID,
  canToggleParticipantLayoutMutedState,
  createParticipantLayoutScreenShareParticipant,
  getParticipantLayoutInitials,
  sortParticipantLayoutParticipants
} from "./participant-layout-participants";

const AUTO_LAYOUT_MODE = "auto";
const GRID_LAYOUT_MODE = "grid";
const SPOTLIGHT_LAYOUT_MODE = "spotlight";
const MAX_PARTICIPANTS = 12;
const MOCK_NAMES = [
  "Angel Cabrera De La Vega Meeting Host With A Long Name",
  "Camila Valentina Rodriguez Product Design Reviewer",
  "Daniel",
  "Mia",
  "Sofia",
  "Noah",
  "Zoe",
  "Lucas",
  "Elena",
  "Mateo",
  "Ava",
  "Leo"
];

function normalizeParticipantCount(participantCount) {
  if (!Number.isFinite(participantCount)) {
    return 0;
  }

  return Math.min(MAX_PARTICIPANTS, Math.max(0, Math.floor(participantCount)));
}

function getDisplayName(index) {
  const baseName = MOCK_NAMES[index % MOCK_NAMES.length];
  const cycle = Math.floor(index / MOCK_NAMES.length);

  return cycle === 0 ? baseName : `${baseName} ${cycle + 1}`;
}

function createMockParticipant(index) {
  const displayName = getDisplayName(index);
  const participant = {
    id: `participant-${index + 1}`,
    kind: PARTICIPANT_TILE_KIND,
    displayName,
    initials: getParticipantLayoutInitials(displayName),
    isMuted: index % 3 === 0,
    isSpeaking: false,
    isPinned: false,
    isHandRaised: false,
    isSelf: index === 0,
    role: index === 0 ? ROOM_ADMIN_ROLE_LABEL : null
  };

  return {
    ...participant,
    canToggleMuted: canToggleParticipantLayoutMutedState({ canMuteUsers: true, participant })
  };
}

function buildMockParticipants(participantCount) {
  const safeCount = normalizeParticipantCount(participantCount);

  return Array.from({ length: safeCount }, (_, index) => createMockParticipant(index));
}

function isSupportedLayoutMode(mode) {
  return mode === AUTO_LAYOUT_MODE || mode === GRID_LAYOUT_MODE || mode === SPOTLIGHT_LAYOUT_MODE;
}

function decorateParticipantsForLayout(participants, pinnedTileId, screenShareState) {
  const screenSharePinnedTileId =
    screenShareState.active && pinnedTileId === SCREEN_SHARE_TILE_ID && participants.length === 1 ? null : pinnedTileId;
  const layoutParticipants = participants.map(participant => {
    const nextParticipant = {
      ...participant,
      isPinned: participant.id === pinnedTileId
    };

    return {
      ...nextParticipant,
      canToggleMuted: canToggleParticipantLayoutMutedState({ canMuteUsers: true, participant: nextParticipant })
    };
  });

  if (screenShareState.active) {
    layoutParticipants.push(
      createParticipantLayoutScreenShareParticipant({
        participants,
        ownerId: screenShareState.ownerId,
        pinnedTileId: screenSharePinnedTileId
      })
    );
  }

  return sortParticipantLayoutParticipants(layoutParticipants);
}

export function useParticipantLayoutMockState() {
  const [mode, setModeState] = useState(AUTO_LAYOUT_MODE);
  const [participants, setParticipants] = useState(() => buildMockParticipants(0));
  const [pinnedTileId, setPinnedTileId] = useState(null);
  const [screenShareState, setScreenShareState] = useState({ active: false, ownerId: null });

  const reset = useCallback(() => {
    setModeState(AUTO_LAYOUT_MODE);
    setParticipants(buildMockParticipants(0));
    setPinnedTileId(null);
    setScreenShareState({ active: false, ownerId: null });
  }, []);

  const setCount = useCallback(
    participantCount => {
      const nextParticipants = buildMockParticipants(participantCount);
      const hasPinnedParticipant = nextParticipants.some(participant => participant.id === pinnedTileId);

      if (!hasPinnedParticipant && pinnedTileId && pinnedTileId !== SCREEN_SHARE_TILE_ID) {
        setPinnedTileId(null);
      }

      setScreenShareState(currentScreenShareState => {
        if (!currentScreenShareState.active) {
          return currentScreenShareState;
        }

        return {
          ...currentScreenShareState,
          ownerId: nextParticipants.find(participant => participant.id === currentScreenShareState.ownerId)?.id || null
        };
      });

      setParticipants(nextParticipants);
    },
    [pinnedTileId]
  );

  const addParticipant = useCallback(() => {
    setParticipants(currentParticipants => buildMockParticipants(currentParticipants.length + 1));
  }, []);

  const removeParticipant = useCallback(() => {
    setParticipants(currentParticipants => {
      const nextParticipants = buildMockParticipants(currentParticipants.length - 1);

      if (
        pinnedTileId &&
        pinnedTileId !== SCREEN_SHARE_TILE_ID &&
        !nextParticipants.some(participant => participant.id === pinnedTileId)
      ) {
        setPinnedTileId(null);
      }

      setScreenShareState(currentScreenShareState => {
        if (!currentScreenShareState.active) {
          return currentScreenShareState;
        }

        return {
          ...currentScreenShareState,
          ownerId: nextParticipants.find(participant => participant.id === currentScreenShareState.ownerId)?.id || null
        };
      });

      return nextParticipants;
    });
  }, [pinnedTileId]);

  const setMode = useCallback(nextMode => {
    setModeState(currentMode => (isSupportedLayoutMode(nextMode) ? nextMode : currentMode));
  }, []);

  const startScreenShare = useCallback(() => {
    setScreenShareState(currentScreenShareState => {
      if (currentScreenShareState.active) {
        return currentScreenShareState;
      }

      setPinnedTileId(SCREEN_SHARE_TILE_ID);

      return {
        active: true,
        ownerId: participants[0]?.id || null
      };
    });
  }, [participants]);

  const stopScreenShare = useCallback(() => {
    setScreenShareState({ active: false, ownerId: null });
    setPinnedTileId(currentPinnedTileId => (currentPinnedTileId === SCREEN_SHARE_TILE_ID ? null : currentPinnedTileId));
  }, []);

  const toggleScreenShare = useCallback(() => {
    if (screenShareState.active) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
  }, [screenShareState.active, startScreenShare, stopScreenShare]);

  const toggleParticipantMuted = useCallback(participantId => {
    setParticipants(currentParticipants =>
      currentParticipants.map(participant => {
        if (participant.id !== participantId || participant.isMuted) {
          return participant;
        }

        const nextParticipant = { ...participant, isMuted: true };

        return {
          ...nextParticipant,
          canToggleMuted: canToggleParticipantLayoutMutedState({ canMuteUsers: true, participant: nextParticipant })
        };
      })
    );
  }, []);

  const toggleParticipantPinned = useCallback(participantId => {
    setPinnedTileId(currentPinnedTileId => (currentPinnedTileId === participantId ? null : participantId));
  }, []);

  const layoutParticipants = useMemo(
    () => decorateParticipantsForLayout(participants, pinnedTileId, screenShareState),
    [participants, pinnedTileId, screenShareState]
  );

  const debugControls = useMemo(
    () => ({
      reset,
      setCount,
      addParticipant,
      removeParticipant,
      setMode,
      startScreenShare,
      stopScreenShare,
      toggleScreenShare
    }),
    [reset, setCount, addParticipant, removeParticipant, setMode, startScreenShare, stopScreenShare, toggleScreenShare]
  );

  return {
    mode,
    participants: layoutParticipants,
    debugControls,
    toggleParticipantMuted,
    toggleParticipantPinned
  };
}
